import { createInstance, Identify, Types } from '@amplitude/analytics-browser';
import type { SessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { analyticsConfig } from '../featureAnalytics';
import { analyticsDiagnostics } from './diagnostics';
import { configureContextRuntime, syncAccountScope, sameAccount, AccountScope, getPageView } from './context';
import {
  ANALYTICS_V2_VERSION, AttributionContext, FrontendEventPropertiesV2,
  FrontendEventV2, validateSemanticEvent, projectAttributionContext,
} from './contract-v2';
import {
  ANALYTICS_VERSION,
  canonicalPage,
  FRONTEND_EVENTS,
  FrontendEvent,
  REPLAY_FEATURE,
  safeProperties,
} from './contract';

export interface AnalyticsIdentity {
  deviceId: string;
  sessionId: number;
  userId?: string;
  lastActivity: number;
  clientId?: number | null;
  accountEpoch?: string;
}
export type AnalyticsTrackOptions = Partial<AnalyticsIdentity> & {
  insertId?: string;
  executionKind?: 'interactive' | 'deferred' | 'automatic';
  factKey?: string;
};
export type TrackDisposition = 'queued' | 'duplicate' | 'disabled' | 'invalid' | 'dropped' | 'stale';
export type SemanticContext = AttributionContext & { search_id?: string; operation_id?: string; accountScope?: AccountScope };
type AutoEnvelope = 'schema_version' | 'source' | 'environment' | 'release_version';
export type SemanticProperties<E extends FrontendEventV2> = Omit<FrontendEventPropertiesV2[E], AutoEnvelope>;
export interface AnalyticsClientConfig {
  enabled: boolean;
  apiKey: string;
  serverZone: 'US' | 'EU';
  environment: string;
  replayEnabled: boolean;
  releaseVersion?: string;
  projectFingerprint?: string;
}

function semanticFactKey(event: FrontendEventV2, value: Record<string, any>): string | undefined {
  if (event === 'page_engagement') return undefined; // each measured interval is a distinct fact
  if (value.attempt_id && value.resource && value.contact_type)
    return `${value.attempt_id}:${value.resource.namespace}:${value.resource.id}:${value.contact_type}`;
  if (event === 'search_submitted') return value.search_id;
  if (event === 'page_viewed') return value.page_view_id;
  return value.detail_view_id || value.tab_view_id || value.view_id ||
    value.selection?.selection_id || value.action_id ||
    (event === 'network_failed' ? value.operation_id : undefined);
}
type AnalyticsUser = {
  id: number | string;
  fullname?: string | null;
  email?: string | null;
  role?: string;
  user_type?: string;
  client?: { id?: number; name?: string; slug?: string };
  current_subscription?: { id?: number; name?: string };
  feature_flags?: Record<string, boolean>;
};
export const IDENTITY_CHANGED = 'aydentify:analytics-identity';
const SESSION_TIMEOUT = 30 * 60 * 1000;
export function newId(): string {
  try {
    if (crypto.randomUUID) return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    // Telemetry IDs are not credentials. Older/insecure browser contexts must
    // still be able to use the app when Web Crypto is unavailable.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const value = Math.floor(Math.random() * 16);
      return (c === 'x' ? value : (value & 3) | 8).toString(16);
    });
  }
}

// Autocapture must not forward URL query strings, DOM text or input attributes.
// Keep experience measurements; submitted terms use the explicit search event.
export function automaticProperties(properties: Record<string, unknown> = {}) {
  return safeProperties(
    Object.fromEntries(
      Object.entries(properties).filter(
        ([key, value]) =>
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          /^\[Amplitude\] (LCP|FCP|INP|CLS|TTFB)$/.test(key),
      ),
    ),
  );
}

// A bounded offline queue. All stored records have already passed the event
// projection; no credentials or raw request/response bodies are stored here.
export class EventStorage implements Types.Storage<Types.Event[]> {
  private values = new Map<string, Types.Event[]>();
  async isEnabled() {
    return true;
  }
  async get(key: string) {
    try {
      const saved = localStorage.getItem(`aydentify:events:${key}`);
      if (saved) {
        const events = JSON.parse(saved);
        if (!Array.isArray(events)) return this.values.get(key);
        if (events.length > 500) analyticsDiagnostics.increment('offline_persistence_truncated', events.length - 500);
        return events.slice(-500);
      }
    } catch {
      analyticsDiagnostics.increment('storage_unavailable');
    }
    return this.values.get(key);
  }
  async getRaw(key: string) {
    const value = await this.get(key);
    return value ? JSON.stringify(value) : undefined;
  }
  async set(key: string, value: Types.Event[]) {
    if (value.length > 500) analyticsDiagnostics.increment('offline_persistence_truncated', value.length - 500);
    const bounded = value.slice(-500);
    this.values.set(key, bounded);
    try {
      localStorage.setItem(`aydentify:events:${key}`, JSON.stringify(bounded));
    } catch {
      analyticsDiagnostics.increment('storage_unavailable');
    }
  }
  async remove(key: string) {
    this.values.delete(key);
    try {
      localStorage.removeItem(`aydentify:events:${key}`);
    } catch {
      /* ignored */
    }
  }
  async reset() {
    for (const key of this.values.keys()) await this.remove(key);
  }
}

export class AnalyticsClient {
  private identity?: AnalyticsIdentity;
  private user?: AnalyticsUser;
  private verified = false;
  private ready = false;
  private initializing?: Promise<void>;
  private pending = 0;
  private epoch = 0;
  private page = '/other';
  private replay?: SessionReplayPlugin;
  private replayTask: Promise<void> = Promise.resolve();
  private replayScheduled = false;
  private traits = '';
  private readonly facts = new Set<string>();
  private readonly storageKey: string;

  constructor(
    readonly config: AnalyticsClientConfig = analyticsConfig,
    private readonly sdk = createInstance(),
    private readonly loadReplay = () => import('@amplitude/plugin-session-replay-browser'),
  ) {
    this.storageKey = `aydentify:analytics:${config.apiKey.slice(-10)}:${config.environment}`;
  }

  get enabled() {
    return this.config.enabled && !!this.config.apiKey && typeof window !== 'undefined';
  }

  initialize(): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    if (this.initializing) return this.initializing;
    this.identity = this.readIdentity() || this.freshIdentity();
    // Never restore a persisted actor until /currentUser verifies the cookie.
    const initial = this.identity;
    this.initializing = (async () => {
      try {
        await this.sdk.init(this.config.apiKey, undefined, {
          minIdLength: 1,
          deviceId: initial.deviceId,
          sessionId: initial.sessionId,
          identityStorage: 'none',
          storageProvider: new EventStorage(),
          // The application owns inactivity rollover so API and browser events
          // cannot independently select different sessions.
          sessionTimeout: Number.MAX_SAFE_INTEGER,
          optOut: true,
          serverZone: this.config.serverZone,
          remoteConfig: { fetchRemoteConfig: false },
          flushQueueSize: 30,
          flushIntervalMillis: 5000,
          flushMaxRetries: 2,
          logLevel: Types.LogLevel.Error,
          autocapture: {
            sessions: true,
            webVitals: true,
            frustrationInteractions: { rageClicks: true, deadClicks: true },
            pageViews: false,
            pageUrlEnrichment: false,
            attribution: false,
            elementInteractions: false,
            formInteractions: false,
            fileDownloads: false,
            networkTracking: false,
          },
        }).promise;
        await this.sdk.add({
          name: 'aydentify-event-boundary',
          type: 'enrichment',
          setup: async () => undefined,
          execute: async (event) => {
            if (!this.verified) return null;
            if (
              !event.event_type.startsWith('[Amplitude]') &&
              !['session_start', 'session_end'].includes(event.event_type)
            )
              return event;
            if (this.page.startsWith('/user/')) return null;
            return {
              ...event,
              event_properties: {
                ...automaticProperties({ ...event.event_properties }),
                page: this.page,
                source: 'frontend',
                environment: this.config.environment,
                release_version: this.config.releaseVersion || 'unversioned',
                schema_version: ANALYTICS_VERSION,
              },
            };
          },
        }).promise;
        this.ready = true;
        if (this.verified) this.applyIdentity();
        window.addEventListener('storage', this.onStorage);
        this.notify();
      } catch {
        analyticsDiagnostics.increment('sdk_initialization_failed');
      }
    })();
    return this.initializing;
  }

  private freshIdentity(): AnalyticsIdentity {
    return { deviceId: newId(), sessionId: Date.now(), lastActivity: Date.now(), accountEpoch: newId() };
  }
  private readIdentity(): AnalyticsIdentity | undefined {
    try {
      const value = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      if (
        value &&
        typeof value.deviceId === 'string' &&
        /^[a-zA-Z0-9-]{16,128}$/.test(value.deviceId) &&
        Number.isSafeInteger(value.sessionId) &&
        Number.isFinite(value.lastActivity)
      )
        return {
          deviceId: value.deviceId,
          sessionId: value.sessionId,
          lastActivity: value.lastActivity,
          userId: typeof value.userId === 'string' ? value.userId : undefined,
          clientId: Number.isSafeInteger(value.clientId) ? value.clientId : undefined,
          accountEpoch: typeof value.accountEpoch === 'string' && /^[a-zA-Z0-9-]{16,128}$/.test(value.accountEpoch)
            ? value.accountEpoch : newId(),
        };
    } catch {
      /* memory-only identity */
    }
    return undefined;
  }
  private persist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.identity));
    } catch {
      /* memory-only identity */
    }
  }
  private notify() {
    // The singleton binding handles scope changes for every attribution helper.
    // Test-created clients do not replace the application's runtime binding.
    if (this === analytics) syncAccountScope();
    window.dispatchEvent(new Event(IDENTITY_CHANGED));
  }

  private onStorage = (event: StorageEvent) => {
    if (event.key !== this.storageKey) return;
    const next = this.readIdentity();
    if (!next) return;
    if (this.verified && (next.userId !== this.identity?.userId ||
      next.clientId !== this.identity?.clientId || next.accountEpoch !== this.identity?.accountEpoch)) {
      // Another tab changed the cookie's account. Suspend until authentication is
      // revalidated rather than claiming the old tab belongs to the new actor.
      this.verified = false;
      this.epoch++;
      this.stopReplay();
      this.sdk.setOptOut(true);
      this.user = undefined;
    }
    this.identity = next;
    if (this.verified && this.ready) this.applyIdentity();
    this.notify();
  };

  setUser(user?: AnalyticsUser): void {
    if (!this.enabled) return;
    void this.initialize();
    try {
      const nextUserId = user?.id !== undefined && user.id !== null ? String(user.id) : undefined;
      const nextClientId = Number.isSafeInteger(user?.client?.id) ? user?.client?.id : undefined;
      const oldUserId = this.identity?.userId;
      if (oldUserId && (oldUserId !== nextUserId || this.identity?.clientId !== nextClientId)) {
        this.epoch++;
        this.stopReplay();
        this.identity = this.freshIdentity();
        this.traits = '';
        this.facts.clear();
        if (this.ready) this.sdk.reset();
      }
      this.user = user;
      this.identity = { ...(this.identity || this.freshIdentity()), userId: nextUserId, clientId: nextClientId };
      this.verified = true;
      this.persist();
      if (this.ready) this.applyIdentity();
      this.notify();
    } catch {
      /* No authentication behavior depends on analytics. */
    }
  }

  reset() {
    this.setUser(undefined);
  }

  private applyIdentity() {
    if (!this.identity || !this.verified) return;
    this.sdk.setDeviceId(this.identity.deviceId);
    this.sdk.setUserId(this.identity.userId);
    this.sdk.setSessionId(this.identity.sessionId);
    // A suspended old replay must be removed before re-enabling the SDK;
    // otherwise the plugin's opt-in callback could briefly restart it.
    if (this.replay && this.sdk.getOptOut()) {
      void this.replayTask
        .then(() => {
          if (this.verified) this.sdk.setOptOut(false);
        })
        .catch(() => undefined);
    } else this.sdk.setOptOut(false);
    const traits = safeProperties({
      // Profile metadata comes only from the authenticated currentUser response.
      // The stable internal ID remains the stitching key on both SDKs.
      name:
        typeof this.user?.fullname === 'string'
          ? this.user.fullname.trim() || undefined
          : undefined,
      email: typeof this.user?.email === 'string' ? this.user.email.trim() || undefined : undefined,
      user_type: this.user?.user_type,
      role: this.user?.role,
      is_internal: this.user?.role === 'admin',
      client_id: this.user?.client?.id,
      client_name: this.user?.client?.name,
      client_slug: this.user?.client?.slug,
      plan: this.user?.current_subscription?.name,
    });
    const signature = JSON.stringify(traits);
    if (this.user && signature !== this.traits) {
      this.traits = signature;
      const identify = new Identify();
      for (const [key, value] of Object.entries(traits)) identify.set(key, value);
      // Clear traits that may disappear when the authenticated profile changes.
      for (const key of ['name', 'email', 'client_id', 'client_name', 'client_slug', 'plan'])
        if (!(key in traits)) identify.unset(key);
      void this.sdk
        .identify(identify, {
          user_id: this.identity.userId,
          device_id: this.identity.deviceId,
          session_id: this.identity.sessionId,
        })
        .promise.catch(() => undefined);
    }
    this.syncReplay();
  }

  snapshot(): AnalyticsIdentity | undefined {
    if (!this.enabled) return undefined;
    void this.initialize();
    if (!this.identity) return undefined;
    return { ...this.identity, userId: this.verified ? this.identity.userId : undefined };
  }

  touch(): void {
    if (!this.enabled || !this.identity) return;
    try {
      const now = Date.now();
      let rolledOver = false;
      if (now - this.identity.lastActivity >= SESSION_TIMEOUT) {
        this.identity.sessionId = now;
        rolledOver = true;
        if (this.ready) this.sdk.setSessionId(now);
      }
      // Throttle storage writes from pointer/scroll/keyboard activity.
      if (now - this.identity.lastActivity >= 1000) {
        this.identity.lastActivity = now;
        if (this.ready) this.sdk.extendSession();
        this.persist();
      }
      if (rolledOver) this.notify();
    } catch {
      /* Activity tracking cannot interrupt event handlers. */
    }
  }

  setPage(path: string) {
    this.page = canonicalPage(path);
    this.syncReplay();
  }
  canTrack() {
    return this.enabled && this.ready && this.verified;
  }

  /** Only authenticated, verified identity may own cross-page attribution. */
  contextIdentity(): AnalyticsIdentity | undefined {
    return this.verified && this.identity?.userId ? { ...this.identity } : undefined;
  }

  isCurrentActor(identity: Partial<AnalyticsIdentity>): boolean {
    return this.verified && identity.userId === this.identity?.userId &&
      identity.deviceId === this.identity?.deviceId &&
      (identity.accountEpoch === undefined || identity.accountEpoch === this.identity?.accountEpoch) &&
      (identity.clientId === undefined || (identity.clientId ?? null) === (this.identity?.clientId ?? null));
  }

  track(
    event: FrontendEvent,
    properties: Record<string, unknown> = {},
    options?: AnalyticsTrackOptions,
  ): void {
    if (!this.canTrack() || !FRONTEND_EVENTS.includes(event)) return;
    this.send(event, properties, options, ANALYTICS_VERSION);
  }

  /** Version two is a typed and validated boundary; legacy track stays schema one. */
  trackSemantic<E extends FrontendEventV2>(
    event: E,
    context: SemanticContext,
    properties: SemanticProperties<E>,
    options?: AnalyticsTrackOptions,
  ): TrackDisposition {
    if (!this.canTrack()) return 'disabled';
    try {
      const executionKind = options?.executionKind || context.execution_kind || 'interactive';
      const hasIdentityOverride = options && ['userId', 'deviceId', 'clientId', 'sessionId', 'accountEpoch']
        .some((field) => Object.prototype.hasOwnProperty.call(options, field));
      if (hasIdentityOverride && (
        !options.accountEpoch || options.accountEpoch !== this.identity?.accountEpoch ||
        options.userId !== this.identity?.userId || options.deviceId !== this.identity?.deviceId ||
        (options.clientId ?? null) !== (this.identity?.clientId ?? null) ||
        !Number.isSafeInteger(options.sessionId) || (options.sessionId || 0) <= 0
      )) {
        analyticsDiagnostics.increment('stale_context');
        return 'stale';
      }
      const { accountScope, ...attribution } = context;
      if (accountScope && !sameAccount(accountScope, this.contextIdentity() as AccountScope)) {
        analyticsDiagnostics.increment('stale_context');
        return 'stale';
      }
      // Interactive facts always use the current session; only genuinely captured
      // automatic/deferred executions may override it with an older session.
      if (executionKind === 'interactive') this.touch();
      const eventOptions = hasIdentityOverride && executionKind === 'interactive'
        ? { ...options, sessionId: this.identity!.sessionId } : options;
      const projected = projectAttributionContext(attribution);
      if (!projected.valid) {
        analyticsDiagnostics.increment('schema_rejected');
        return 'invalid';
      }
      const currentPage = executionKind === 'interactive' ? getPageView() : undefined;
      const result = validateSemanticEvent(event, {
        ...properties, ...projected.value,
        ...(context.search_id ? { search_id: context.search_id } : {}),
        ...(context.operation_id ? { operation_id: context.operation_id } : {}),
        ...(currentPage && event !== 'page_viewed' ? { page_view_id: currentPage.id } : {}),
        schema_version: ANALYTICS_V2_VERSION,
        source: 'frontend',
        environment: this.config.environment,
        release_version: this.config.releaseVersion || 'unversioned',
        client_id: this.user?.client?.id,
        user_type: this.user?.user_type,
        role: this.user?.role,
        is_internal: this.user ? this.user.role === 'admin' : undefined,
        page: typeof (properties as Record<string, unknown>).page === 'string'
          ? canonicalPage((properties as Record<string, unknown>).page as string) : this.page,
      });
      if (!result.valid) {
        analyticsDiagnostics.increment('schema_rejected');
        return 'invalid';
      }
      const factKey = options?.factKey || semanticFactKey(event, result.value);
      const factId = options?.insertId || (factKey ? `${event}:${factKey}` : undefined);
      const key = factId && `${this.identity?.accountEpoch}:${factId}`;
      if (key && this.facts.has(key)) {
        analyticsDiagnostics.increment('duplicate_suppressed');
        return 'duplicate';
      }
      const disposition = this.send(event, { ...result.value }, {
        ...eventOptions,
        insertId: factId || options?.insertId,
        executionKind,
      }, ANALYTICS_V2_VERSION);
      if (disposition === 'queued' && key) {
        if (this.facts.size >= 1000) this.facts.delete(this.facts.values().next().value!);
        this.facts.add(key);
      }
      return disposition;
    } catch {
      analyticsDiagnostics.increment('schema_rejected');
      return 'invalid';
    }
  }

  private send(
    event: string, properties: Record<string, unknown>,
    options: AnalyticsTrackOptions | undefined, schemaVersion: number,
  ): TrackDisposition {
    if (this.pending >= 500) {
      analyticsDiagnostics.increment('dropped_queue_capacity');
      return 'dropped';
    }
    if (options?.accountEpoch && !this.isCurrentActor({ ...this.identity, ...options })) {
      analyticsDiagnostics.increment('stale_context');
      return 'stale';
    }
    try {
      if (schemaVersion === ANALYTICS_VERSION && event !== 'page_engagement' &&
        (!options?.executionKind || options.executionKind === 'interactive')) this.touch();
      const actor = { ...this.identity!, ...options };
      const user = actor.userId === this.identity?.userId ? this.user : undefined;
      this.pending++;
      analyticsDiagnostics.setPending(this.pending);
      const result = this.sdk.track(
        event,
        schemaVersion === ANALYTICS_V2_VERSION ? properties : safeProperties({
          ...properties,
          schema_version: schemaVersion,
          source: 'frontend',
          environment: this.config.environment,
          release_version: this.config.releaseVersion || 'unversioned',
          page: typeof properties.page === 'string' ? canonicalPage(properties.page) : this.page,
          user_type: user?.user_type,
          role: user?.role,
          client_id: user?.client?.id,
          is_internal: user ? user.role === 'admin' : undefined,
        }),
        {
          user_id: actor.userId,
          device_id: actor.deviceId,
          session_id: actor.sessionId,
          insert_id: options?.insertId || newId(),
          time: Date.now(),
        },
      );
      analyticsDiagnostics.increment('queued');
      void result.promise
        .then((result) => analyticsDiagnostics.classifyResult(result.code))
        .catch(() => analyticsDiagnostics.increment('sdk_promise_rejected'))
        .finally(() => {
          this.pending--;
          analyticsDiagnostics.setPending(this.pending);
        });
      return 'queued';
    } catch {
      this.pending = Math.max(0, this.pending - 1);
      analyticsDiagnostics.setPending(this.pending);
      analyticsDiagnostics.increment('sdk_threw');
      return 'dropped';
    }
  }

  flush() {
    if (this.ready) {
      try {
        void this.sdk.flush().promise.catch(() => undefined);
      } catch {
        /* ignored */
      }
    }
  }

  dispose() {
    this.epoch++;
    this.verified = false;
    if (this === analytics) syncAccountScope();
    this.stopReplay();
    window.removeEventListener('storage', this.onStorage);
    if (this.ready) this.sdk.setOptOut(true);
  }

  private wantsReplay() {
    return (
      this.canTrack() &&
      this.config.replayEnabled &&
      !!this.user &&
      this.user.feature_flags?.[REPLAY_FEATURE] === true &&
      this.page !== '/user/login'
    );
  }

  private stopReplay() {
    try {
      this.replay?.stop();
    } catch {
      /* SDK failure must not affect logout. */
    }
    const plugin = this.replay;
    this.replayTask = this.replayTask
      .then(async () => {
        try {
          if (plugin) await this.sdk.remove(plugin.name).promise;
        } finally {
          if (this.replay === plugin) this.replay = undefined;
        }
      })
      .catch(() => undefined)
      .then(() => this.syncReplay());
  }

  private syncReplay() {
    if (!this.wantsReplay()) {
      if (this.replay) this.stopReplay();
      return;
    }
    if (this.replay || this.replayScheduled) return;
    this.replayScheduled = true;
    const epoch = this.epoch;
    const start = () => {
      this.replayTask = this.replayTask
        .then(async () => {
          if (epoch !== this.epoch || !this.wantsReplay()) return;
          const { sessionReplayPlugin } = await this.loadReplay();
          if (epoch !== this.epoch || !this.wantsReplay()) return;
          const plugin = sessionReplayPlugin({
            sampleRate: 1,
            useWebWorker: true,
            forceSessionTracking: false,
            performanceConfig: { enabled: true },
            maxPersistedEventsSizeBytes: 5 * 1024 * 1024,
            maxSingleEventSizeBytes: 512 * 1024,
            sendTimeoutMs: 5000,
            privacyConfig: {
              defaultMaskLevel: 'medium',
              blockSelector: '[data-analytics-private], .StripeElement, iframe[src*="stripe"]',
            },
          });
          const setup = plugin.setup.bind(plugin);
          plugin.setup = async (config, client) => {
            if (epoch !== this.epoch || !this.wantsReplay()) return;
            await setup(config, client);
            if (epoch !== this.epoch || !this.wantsReplay()) plugin.stop();
          };
          const execute = plugin.execute?.bind(plugin);
          if (execute)
            plugin.execute = async (event) => {
              // A queued event from the previous account must never move the new
              // account's recorder back into the previous device/session.
              if (
                epoch !== this.epoch ||
                !this.wantsReplay() ||
                event.device_id !== this.identity?.deviceId ||
                event.user_id !== this.identity?.userId
              )
                return event;
              return execute(event);
            };
          this.replay = plugin;
          await this.sdk.add(plugin).promise;
          if (epoch !== this.epoch || !this.wantsReplay()) {
            plugin.stop();
            await this.sdk.remove(plugin.name).promise;
            if (this.replay === plugin) this.replay = undefined;
          }
        })
        .catch(() => undefined)
        .finally(() => {
          this.replayScheduled = false;
          if (epoch !== this.epoch && this.wantsReplay()) this.syncReplay();
        });
    };
    const schedule = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
      else setTimeout(start, 0);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }
}

export const analytics = new AnalyticsClient();
configureContextRuntime({
  snapshot: () => analytics.contextIdentity() as ReturnType<Parameters<typeof configureContextRuntime>[0]['snapshot']>,
  touch: () => analytics.touch(),
  newId,
  diagnostic: (reason) => analyticsDiagnostics.increment(
    reason === 'handoff_rejected' ? 'invalid_context' : reason,
  ),
});
